import { CheckCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
import {
  AnalyticsEvent,
  AnalyticsEventNameType,
  FeatureFlag,
  SupportEmailType,
  WebDashboardTabsConfig,
  WebDashboardTrialStateType,
} from "@coeff/api";
import { isWebDashboardTrialExpired } from "@coeff/utils";
import { useFeatureGate } from "@statsig/react-bindings";
import * as Comlink from "comlink";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useLoginWithRedirect } from "../../../src/utils/useLoginWithRedirect";
import {
  parseRestoreError,
  useArchiveWebDashboardMutation,
  useDeleteWebDashboardMutation,
  useWebDashboards,
} from "../../api";
import { analyticsTrackAnonymous, getAnonymousUserId } from "../../api/track";
import {
  CoeLogo,
  CoeLogoWhiteText,
  CoeLogoWhiteLogoAndText,
  ExcelSheetsIcon,
  HamburgerMenuIcon,
  Modal,
  message,
} from "../../components";
import { Tooltip, TooltipDirection } from "../../components/Tooltip/index";
import { useSlackOAuth } from "../../hooks/useSlackOAuth";
import { AppState } from "../../store";
import { useApiContext, useTrack } from "../../utils";
import { AUTH_SEARCH_PARAMS_KEY } from "../AuthGuard";
import { submitWebDashboardProactiveSalesForm } from "../Billing/billing.utils";
import { DeleteDashboardModal, PlanLimitUsageBar } from "../Dashboard/components";

import { ChatSidebarFlexSpacer } from "./ChatSidebarFlexSpacer";
import { getAiDashboardsHelpVideoUrl } from "./dashboardHelpVideo";
import { DashboardHelpVideoModal } from "./DashboardHelpVideoModal";
import {
  DashboardSupportCategory,
  DashboardSupportFeedbackModal,
} from "./DashboardSupportFeedbackModal";
import type { ExcelUploadWorkerApi } from "./excelUploadWorker";
import { GradientAIButton } from "./GradientAIButton";
import { MyDashboardsPopover } from "./MyDashboardsPopover";
import { NavBarLoginButton } from "./NavBarLoginButton";
import { NavBarMoreMenu } from "./NavBarMoreMenu";
import { ShareDashboardModal } from "./ShareDashboardModal";
import { SlackAuthModal } from "./SlackAuthModal";
import { SourceSheetModal } from "./SourceSheetModal";
import { SubscribeDashboardModal } from "./SubscribeDashboardModal";
import { resolveTheme } from "./themes";
import type { ThemeBackground, ThemeMode } from "./themes";
import { ToastProps } from "./Toast";
import { UserIcon } from "./UserIcon";
import { UserMemoryDrawer } from "./UserMemoryDrawer";
import { UserMenuDropdown } from "./UserMenuDropdown";
import { useViewTransitionStateUpdate } from "./useViewTransitionStateUpdate";
import { useDashboard, useDashboardPermissions } from "./webDashboard";
import {
  WebDashboardNavIconAiSparkle,
  WebDashboardNavIconMyDashboards,
  WebDashboardNavIconShare,
  WebDashboardNavIconSourceSheet,
  WebDashboardNavIconSubscribe,
} from "./WebDashboardNavIcons";
import { useWebDashboardViewerContext } from "./WebDashboardViewerContext";

// Matches the Figma modal: soft "shadow/small", 8px radius, 20px body padding. Title renders
// inside the body column (no separate AntD header).
const DashboardModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    box-shadow: 0px 0px 16px #ebebeb, 0px 16px 24px #ebebeb;
  }

  .ant-modal-body {
    padding: 20px;
  }
`;

const DashboardModalTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #111827;
`;

/** Matches Share + Theme row: `themed-nav-button` + 13px medium label + 24×24 icon slot. */
const WEB_DASH_NAV_TEXT_ACTION_CLASS =
  "themed-nav-button gap-2 !text-[13px] font-medium leading-[18px]";

/** Breakpoint class for showing nav button labels: `lg` normally, `xl` when sidebar is open. */
const navLabelClass = (sidebarOpen: boolean) =>
  sidebarOpen ? "hidden xl:inline" : "hidden lg:inline";

// Figma mock uses a refresh arrow with a dotted tail.
const DottedRefreshIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    style={{ width: 20, height: 20 }}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Dotted bottom arc */}
    <path
      d="M19.52 14.74A8 8 0 0 1 4.48 14.74"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray="0.1 5.2"
    />

    {/* Solid top arc */}
    <path
      d="M4.48 14.74A8 8 0 0 1 19.52 9.26"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    {/* Arrow head (top-right) */}
    <path
      d="M20.9 6.4v5.4h-5.4"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UploadingSnackbar: React.FC = () => {
  // Note: This snackbar is rendered into the main app document (above the AntD modal mask),
  // where Tailwind may not be available. Use inline styles for consistent appearance.
  return (
    <div
      style={{
        position: "fixed",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: 24,
        zIndex: 3000,
        pointerEvents: "none",
      }}
    >
      <style>
        {`@keyframes coeffUploadingSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
      </style>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          borderRadius: 12,
          background: "var(--theme--color-card-background, #FFFFFF)",
          boxShadow:
            "var(--theme--shadow-card, 0 10px 15px -3px rgba(0, 0, 0, 0.10), 0 4px 6px -4px rgba(0, 0, 0, 0.10))",
          border: "1px solid var(--theme--color-card-border, rgba(0, 0, 0, 0.10))",
          padding: "8px 16px",
        }}
      >
        {/* Coeff AI spinner (matches RefreshSnackbar) */}
        <div style={{ position: "relative", width: 24, height: 24 }}>
          {/* Spinning gradient arc */}
          <svg
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              animation: "coeffUploadingSpin 1s linear infinite",
            }}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="coeff-ai-spinner-grad-upload" x1="4" y1="4" x2="20" y2="20">
                <stop
                  offset="0%"
                  stopColor="var(--theme--color-primary-button-background, #3b82f6)"
                  stopOpacity="0.35"
                />
                <stop
                  offset="55%"
                  stopColor="var(--theme--color-primary-button-background, #3b82f6)"
                  stopOpacity="0.75"
                />
                <stop
                  offset="100%"
                  stopColor="var(--theme--color-primary-button-background, #3b82f6)"
                  stopOpacity="1"
                />
              </linearGradient>
            </defs>
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="url(#coeff-ai-spinner-grad-upload)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="36 20"
              strokeDashoffset="6"
            />
          </svg>

          {/* Static sparkle */}
          <svg
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M12 6.2l1.9 4.3L18.2 12l-4.3 1.5L12 17.8l-1.9-4.3L5.8 12l4.3-1.5L12 6.2z"
              fill="var(--theme--color-primary-button-background, #3b82f6)"
            />
          </svg>
        </div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: "var(--theme--color-card-foreground, #111827)",
          }}
        >
          Uploading…
        </div>
      </div>
    </div>
  );
};

interface WebDashboardNavBarProps {
  dashboardId: string | undefined;
  setToastProps: (props: ToastProps | null) => void;
  handleRefreshDashboard: (
    tabsConfig?: WebDashboardTabsConfig,
    excelUploadS3Key?: string,
    spreadsheetName?: string,
    excelUploadClientMetrics?: {
      file_size_bytes: number;
      upload_type: "post" | "multipart";
      upload_duration_ms: number;
      multipart_part_count?: number;
    }
  ) => void;
  isRefreshing: boolean;
  isCheckingChanges: boolean;
  /** Deletes the given blocks ("Also delete affected charts" in the Disable Tab modal). */
  onDeleteBlocks?: (blockIds: string[]) => void;
  themeId: string;
  /** Selector mode toggle (light/dark) — drives nav chrome + logo variant. */
  mode: ThemeMode;
  /** Selector background toggle (gradient on/off). */
  background: ThemeBackground;
  handleThemeChange: (next: { themeId?: string; mode?: string; background?: string }) => void;
  targetDocument: Document;
  handleSignOut: () => void;
  handleEditButtonClick: (e: React.MouseEvent) => void;
  spreadsheetId?: string | null;
  /** Control theme selector open state externally */
  isThemeSelectorOpen: boolean;
  /** Toggle theme selector open/closed */
  setIsThemeSelectorOpen: (isOpen: boolean) => void;
  /** Live-preview a theme change locally without persisting (selector clicks). */
  onPreviewTheme?: (next: { themeId?: string; mode?: string; background?: string }) => void;
  /** Theme selector closed with changes: show the "Theme updated" toast with Reset. */
  onThemeCommitted?: (revertTo: { themeId: string; mode: string; background: string }) => void;
  onThemeUncommitted?: () => void;
  onThemeDeleted?: (deleted: { themeId: string; name: string }) => void;
  onThemeDeleteFailed?: () => void;
  /** "+ Custom" / "Request custom theme": open the chat with the theme request card. */
  onRequestTheme?: () => void;
  onEditTheme?: (edit: { themeId: string; colors: string[] }) => void;
  /** Whether the chat sidebar is open. Used to offset nav content (spacer), not to hide controls. */
  isChatSidebarOpen?: boolean;
  /** Width of the (resizable) chat sidebar, so the nav spacer and archived banner reserve the right
   * amount of space and stay clear of the fixed sidebar. */
  chatSidebarWidth?: number;
  /** Whether this is a public dashboard (no auth required) - hide refresh/edit, disable Ask button */
  isPublicDashboard?: boolean;
  /** Whether the dashboard is archived - disables the AI chat ("Ask"/"Edit with AI") entrypoint. */
  isArchived?: boolean;
  /** Whether super admin access was granted (internal access) - shows badge with expiry countdown */
  hasSuperAdminAccess?: boolean;
  /** ISO datetime string when super admin access expires (for countdown display) */
  superAdminExpirationDt?: string | null;
  /** Whether view_as_super_admin query param is set - passed to API hooks */
  requestSuperAdminView?: boolean;
  /** Dashboard is still being created; restrict actions that need completed content. */
  isDashboardBuilding?: boolean;
  /** Whether the dashboard has Personalized Views enabled. */
  hasPersonalizedViews?: boolean;
  /** The dashboard owner's trial state (for countdown badge). */
  ownerWebDashboardTrial?: {
    trial_state: WebDashboardTrialStateType;
    trial_state_end_dt?: string | null;
  } | null;
  /** Called when the user clicks "Upgrade" in the trial countdown badge. */
  onProactiveUpgradeClick?: () => void;
  /** Called when the user wants to open chat to set up or remove Personalized Views. */
  onOpenPersonalizedViews?: (message: string) => void;
  /** Current user's full name, used to render the avatar initial. */
  name?: string;
}

export const WebDashboardNavBar: React.FC<WebDashboardNavBarProps> = ({
  dashboardId,
  setToastProps,
  handleRefreshDashboard,
  isRefreshing,
  isCheckingChanges,
  onDeleteBlocks,
  themeId,
  mode,
  background,
  handleThemeChange,
  targetDocument,
  handleSignOut,
  handleEditButtonClick,
  spreadsheetId,
  isThemeSelectorOpen,
  setIsThemeSelectorOpen,
  onPreviewTheme,
  onThemeCommitted,
  onThemeUncommitted,
  onThemeDeleted,
  onThemeDeleteFailed,
  onRequestTheme,
  onEditTheme,
  isChatSidebarOpen = false,
  chatSidebarWidth = 360,
  isPublicDashboard = false,
  isArchived = false,
  hasSuperAdminAccess = false,
  superAdminExpirationDt,
  requestSuperAdminView = false,
  isDashboardBuilding = false,
  hasPersonalizedViews = false,
  ownerWebDashboardTrial,
  onProactiveUpgradeClick,
  onOpenPersonalizedViews,
  name,
}) => {
  const { dashboardMode } = useWebDashboardViewerContext();
  const { apiClient } = useApiContext();

  const { value: enableViewAsFilter } = useFeatureGate(FeatureFlag.EnableViewAsFilter);

  // Get dashboard data from hook
  const { data: dashboardData } = useDashboard(dashboardId, null, requestSuperAdminView);
  const { data: permissions } = useDashboardPermissions(dashboardId, requestSuperAdminView);
  const track = useTrack();

  const loginWithRedirect = useLoginWithRedirect();

  // Local Excel refresh modal state (rendered outside dropdown so it can't unmount).
  const [isLocalExcelUploadModalOpen, setIsLocalExcelUploadModalOpen] = useState(false);
  const [isUploadingLocalExcel, setIsUploadingLocalExcel] = useState(false);
  const [localExcelUploadError, setLocalExcelUploadError] = useState<string | null>(null);
  const [localUploadSelectedFile, setLocalUploadSelectedFile] = useState<File | null>(null);
  const [isDragOverUpload, setIsDragOverUpload] = useState(false);
  const localExcelFileInputRef = useRef<HTMLInputElement | null>(null);
  // Drop-anywhere: highlight + intercept .xlsx/.xlsm drops over the whole dashboard.
  const [isFileDragActive, setIsFileDragActive] = useState(false);

  const updateStateWithViewTransition = useViewTransitionStateUpdate(targetDocument);

  // Derive values from hook data
  const dashboardTitle =
    dashboardData?.content_config?.title || (isDashboardBuilding ? "Building dashboard" : "");
  const canEditDashboard = permissions?.can_edit || false;
  const isAuthenticated = permissions?.is_authenticated || false;
  const userEmail = permissions?.user_email || null;
  const isEditingEnabled = canEditDashboard && dashboardMode === "editor" && !isDashboardBuilding;

  // Archived banner: the owner can restore (unarchive) inline; non-owners are told whom to contact.
  const isOwner = permissions?.is_owner ?? false;
  const ownerEmail = permissions?.owner_email;
  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);
  const isPlanManager = !!clientFlags?.is_plan_manager;
  const accountAdminEmail = clientFlags?.admin_emails?.[0];
  const currentUserWebDashboardTrial = clientFlags?.web_dashboard_trial;
  const isCurrentUserWebDashboardTrialExpired = isWebDashboardTrialExpired(
    currentUserWebDashboardTrial
  );
  const archiveMutation = useArchiveWebDashboardMutation();
  const deleteMutation = useDeleteWebDashboardMutation();
  const history = useHistory();
  // Remaining self-service restores (0 = locked). Known before the restore call so we can confirm
  // on the last one and block once exhausted. Undefined until the dashboard data loads.
  const restoresRemaining = (dashboardData as { restores_remaining?: number } | undefined)
    ?.restores_remaining;

  // Restore-limit states: warning-confirmation on the last restore, locked once exhausted.
  const [showRestoreWarning, setShowRestoreWarning] = useState(false);
  const [showRestoreLocked, setShowRestoreLocked] = useState(false);
  // First-time archive: right after the owner archives here, offer Undo/Delete. On a later visit
  // (page load with the dashboard already archived) the banner defaults to Restore.
  const [justArchived, setJustArchived] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Plan-limit state: restoring would exceed the family's dashboard plan limit (distinct from the
  // per-dashboard restore cap above). Copy/CTA differ for plan managers vs plain domain users.
  const [showRestorePlanLimit, setShowRestorePlanLimit] = useState(false);
  const [planLimitSalesRequestSent, setPlanLimitSalesRequestSent] = useState(false);
  const [planLimitSalesSubmitting, setPlanLimitSalesSubmitting] = useState(false);
  // Plan usage captured from the 409, drives the "X of Y used on your plan" bar.
  const [planLimitUsage, setPlanLimitUsage] = useState<{ used: number; limit: number } | null>(
    null
  );

  // Surface a failed restore in the matching modal (plan limit / restore-locked), else a toast.
  const handleRestoreError = useCallback((err: unknown) => {
    const parsed = parseRestoreError(err);
    if (parsed.kind === "plan_limit") {
      setPlanLimitUsage(parsed.planUsage);
      setPlanLimitSalesRequestSent(false);
      setShowRestorePlanLimit(true);
    } else if (parsed.kind === "restore_locked") {
      setShowRestoreLocked(true);
    } else {
      message.error("Failed to restore dashboard");
    }
  }, []);

  const handleRestoreDashboard = useCallback(async () => {
    if (!dashboardId) {
      return;
    }
    try {
      await archiveMutation.mutateAsync({ web_dashboard_id: dashboardId, archived: false });
      message.success("Dashboard restored");
    } catch (err) {
      handleRestoreError(err);
    }
  }, [dashboardId, archiveMutation, handleRestoreError]);

  // Restore button: block when exhausted, confirm on the last remaining restore, otherwise restore.
  const handleRestoreClick = useCallback(() => {
    if (restoresRemaining !== undefined && restoresRemaining <= 0) {
      setShowRestoreLocked(true);
      return;
    }
    if (restoresRemaining === 1) {
      setShowRestoreWarning(true);
      return;
    }
    void handleRestoreDashboard();
  }, [restoresRemaining, handleRestoreDashboard]);

  // Undo the just-performed archive — a free reversal that doesn't consume a restore.
  const handleUndoArchive = useCallback(async () => {
    if (!dashboardId) {
      return;
    }
    try {
      await archiveMutation.mutateAsync({
        web_dashboard_id: dashboardId,
        archived: false,
        is_undo: true,
      });
      // Leave justArchived as-is: the banner hides once the refetched isArchived flips to false,
      // avoiding a flash of the "Restore" state in the interim.
    } catch (err) {
      handleRestoreError(err);
    }
  }, [dashboardId, archiveMutation, handleRestoreError]);

  const handleDeleteDashboard = useCallback(async () => {
    if (!dashboardId) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(dashboardId);
      message.success("Dashboard deleted");
      setShowDeleteConfirm(false);
      history.push("/dashboards");
    } catch {
      message.error("Failed to delete dashboard");
    }
  }, [dashboardId, deleteMutation, history]);

  const handlePlanLimitContactSales = useCallback(async () => {
    setPlanLimitSalesSubmitting(true);
    const ok = await submitWebDashboardProactiveSalesForm(
      "Dashboard plan limit reached (restore blocked)",
      clientFlags
    );
    setPlanLimitSalesSubmitting(false);
    if (ok) {
      setPlanLimitSalesRequestSent(true);
    } else {
      message.error("Failed to send. Please try again or contact support.");
    }
  }, [clientFlags]);

  // Resolve nav chrome (background, logo variant) against the active Dark/background toggles so
  // the nav bar and logo track the selector, not just the id.
  const currentTheme = resolveTheme(themeId, mode, background);

  // The AI "Ask" button is disabled while the dashboard is building, for unauthenticated viewers
  // of a public dashboard, or for non-editor viewers when Personalized Views is active.
  const isAskButtonDisabled =
    isDashboardBuilding ||
    (isPublicDashboard && !isAuthenticated && !hasSuperAdminAccess) ||
    (hasPersonalizedViews && !canEditDashboard && !hasSuperAdminAccess);

  // Dashboards list backing the "My Dashboards" popover opened from the logo.
  const { data: dashboards, isLoading: isLoadingDashboards } = useWebDashboards({
    enabled: isAuthenticated,
  });

  // Domain-level white-label logo (ENG-8072), resolved server-side from the dashboard owner's
  // domain so share-link/public viewers see it too. Takes precedence over the theme's own
  // customLogo and the Coefficient SVGs. Picks the variant matching the active theme's mode
  // and falls back to the other variant when the matching one is missing (either alone is valid).
  // Note: here "light"/"dark" name the background the artwork is designed for (not artwork color).
  const customBranding = dashboardData?.custom_branding;
  const brandingLogoSrc: string | undefined = customBranding
    ? currentTheme.tailwind.general.isDarkTheme
      ? customBranding.dark_logo_url ?? customBranding.light_logo_url ?? undefined
      : customBranding.light_logo_url ?? customBranding.dark_logo_url ?? undefined
    : undefined;
  // Precedence: domain branding logo > theme.customLogo. Used to decide layout + "Powered by".
  const activeCustomLogo = brandingLogoSrc
    ? {
        src: brandingLogoSrc,
        alt: customBranding?.alt ?? "",
        height: customBranding?.height ?? undefined,
      }
    : currentTheme.customLogo;

  // Local Excel dashboards (no backend-accessible source) refresh via an uploaded .xlsx.
  // Mirrors SourceSheetModal's detection.
  const isLocalExcelDashboard = (() => {
    const isExcel = (dashboardData as any)?.source_type === "excel";
    const excelUrl = (dashboardData as any)?.source_workbook_url as string | undefined;
    const efi = (dashboardData as any)?.excel_file_identifier as
      | { drive_id?: string; not_accessible_from_backend?: boolean }
      | undefined;
    return isExcel && !excelUrl && (!!efi?.not_accessible_from_backend || !efi?.drive_id);
  })();

  // Unified tracking helper that routes to authenticated or anonymous tracking
  const trackNavBarEvent = useCallback(
    (eventName: AnalyticsEventNameType, props: AnalyticsEvent) => {
      if (isAuthenticated) {
        track(eventName, props);
      } else {
        analyticsTrackAnonymous(apiClient, eventName, {
          ...props,
          temp_user_key: getAnonymousUserId(),
        });
      }
    },
    [isAuthenticated, track, apiClient]
  );

  const handleSubscribeClick = () => {
    if (isAuthenticated) {
      setShowSubscribeModal(isOpen => !isOpen);
    } else {
      const redirectParams = {
        // Preserve existing query params across the login round-trip; `openSubscribe` rides
        // separately in searchParams and is consumed as a modal trigger, not merged into the URL.
        url: window.location.pathname + window.location.search,
        searchParams: {
          openSubscribe: "true",
        },
      };
      loginWithRedirect(redirectParams);
    }
  };

  const handleSupportFeedbackSubmit = useCallback(
    (category: DashboardSupportCategory, message: string) => {
      const slicerNames =
        dashboardData?.content_config?.content_spec?.slicers?.map(
          (s: { name: string }) => s.name
        ) || [];
      trackNavBarEvent(AnalyticsEventNameType.VibeDashSupportFeedbackSubmitted, {
        vibe_dash_id: dashboardId,
        ss_id: spreadsheetId || dashboardData?.spreadsheet_id,
        vibe_dash_name: dashboardData?.content_config?.title,
        vibe_dash_slicers: slicerNames,
        vibe_dash_support_category: category,
        vibe_dash_support_message: message,
      });
      apiClient
        .sendSupportEmail({
          send_support_email_request: {
            email_type: SupportEmailType.WebDashboardSupportFeedback,
            email_metadata: {
              email_body: `Category: ${category}

Dashboard: ${dashboardData?.content_config?.title || "—"} (ID: ${dashboardId})

Message:
${message}`,
            },
          },
        })
        .then(() => {
          setToastProps({
            message: "Thanks! Your feedback was received.",
            type: "success",
          });
        })
        .catch(err => {
          console.error("Failed to send support email", err);
          setToastProps({
            message: "Failed to send feedback. Please email support@coefficient.io.",
            type: "error",
          });
        });
    },
    [apiClient, dashboardData, dashboardId, spreadsheetId, trackNavBarEvent, setToastProps]
  );

  // Local state for more menu dropdown
  const [showMoreMenuDropdown, setShowMoreMenuDropdown] = useState(false);
  const moreMenuDropdownRef = useRef<HTMLDivElement>(null);
  const moreMenuButtonRef = useRef<HTMLButtonElement>(null);

  // Local state for user menu dropdown
  const [showUserMenuDropdown, setShowUserMenuDropdown] = useState(false);
  const userMenuDropdownRef = useRef<HTMLDivElement>(null);

  // Local state for source sheet dropdown
  const [showSourceSheetDropdown, setShowSourceSheetDropdown] = useState(false);
  const sourceSheetButtonRef = useRef<HTMLButtonElement>(null);
  const sourceSheetDropdownRef = useRef<HTMLDivElement>(null);

  // "My Dashboards" popover opened from the Coefficient logo
  const [showLogoDashboardsPopover, setShowLogoDashboardsPopover] = useState(false);
  const logoButtonRef = useRef<HTMLButtonElement>(null);
  const logoDashboardsRef = useRef<HTMLDivElement>(null);

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const shareButtonRef = useRef<HTMLButtonElement>(null);
  const shareModalRef = useRef<HTMLDivElement>(null);

  // Subscribe modal state
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const subscribeButtonRef = useRef<HTMLButtonElement>(null);
  const subscribeModalRef = useRef<HTMLDivElement>(null);

  // Slack OAuth state
  const [showSlackAuthModal, setShowSlackAuthModal] = useState(false);

  // Help modals
  const [showSupportFeedbackModal, setShowSupportFeedbackModal] = useState(false);
  const [showAskDisabledTooltip, setShowAskDisabledTooltip] = useState(false);
  const [isAskButtonHovered, setIsAskButtonHovered] = useState(false);
  const [showHelpVideoModal, setShowHelpVideoModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMemoryModal, setShowMemoryModal] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuButtonRef = useRef<HTMLDivElement>(null);

  // Mobile (<640px) nav overflow: below the breakpoint, Subscribe/Ask-Edit/Theme/Rename/Archive
  // are hidden entirely (viewer-only mobile nav, per design), and Source Sheet / Share / My
  // Dashboards / user info+logout are relocated from their desktop buttons into this one
  // hamburger menu. Its items delegate to the SAME handlers/state the desktop buttons already
  // use (handleSourceSheetToggle, handleShare, showLogoDashboardsPopover, handleSignOut) rather
  // than duplicating logic.
  const [showMobileNavMenu, setShowMobileNavMenu] = useState(false);
  const mobileNavButtonRef = useRef<HTMLButtonElement>(null);
  const mobileNavMenuRef = useRef<HTMLDivElement>(null);

  const trialDaysRemaining = (() => {
    const isCountdown =
      ownerWebDashboardTrial?.trial_state === WebDashboardTrialStateType.TrialManualCountdown ||
      ownerWebDashboardTrial?.trial_state === WebDashboardTrialStateType.TrialCountdown;
    if (!isCountdown || !ownerWebDashboardTrial?.trial_state_end_dt) {
      return null;
    }
    const days = Math.max(
      0,
      Math.ceil(
        (new Date(ownerWebDashboardTrial.trial_state_end_dt).getTime() - Date.now()) /
          (1000 * 60 * 60 * 24)
      )
    );
    return days <= 7 ? days : null;
  })();

  const trialExpired = isWebDashboardTrialExpired(ownerWebDashboardTrial);

  // Super admin access countdown timer
  const [superAdminTimeRemaining, setSuperAdminTimeRemaining] = useState<string | null>(null);

  // Open the subscribe modal if the user is returning to the dashboard after opening the subscribe modal
  // while unauthorized and logging in.
  useEffect(() => {
    const authSearchParams = sessionStorage.getItem(AUTH_SEARCH_PARAMS_KEY) || "";
    const searchParams = new URLSearchParams(authSearchParams);
    const shouldOpenSubscribeModal = Boolean(searchParams.get("openSubscribe"));
    if (shouldOpenSubscribeModal) {
      setShowSubscribeModal(true);
      // Clear AUTH_SEARCH_PARAMS sessionStorage item so we don't open the modal every time
      // the page reloads
      sessionStorage.setItem(AUTH_SEARCH_PARAMS_KEY, "");
    }
  }, []);

  useEffect(() => {
    if (!hasSuperAdminAccess || !superAdminExpirationDt) {
      setSuperAdminTimeRemaining(null);
      return;
    }

    // Parse the ISO datetime string to get expiry timestamp
    const expiresAt = new Date(superAdminExpirationDt).getTime();

    const updateCountdown = () => {
      const remaining = expiresAt - Date.now();
      if (remaining <= 0) {
        setSuperAdminTimeRemaining("Expired");
        // Redirect to same dashboard without view_as_super_admin param
        const url = new URL(window.location.href);
        url.searchParams.delete("view_as_super_admin");
        window.location.href = url.toString();
        return;
      }
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      setSuperAdminTimeRemaining(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [hasSuperAdminAccess, superAdminExpirationDt]);

  const handleSlackOAuthSuccess = useCallback(() => {
    // Track Slack connected
    const slicerNames =
      dashboardData?.content_config?.content_spec?.slicers?.map((s: { name: string }) => s.name) ||
      [];
    track(AnalyticsEventNameType.VibeDashSlackConnected, {
      vibe_dash_id: dashboardId,
      ss_id: dashboardData?.spreadsheet_id,
      vibe_dash_name: dashboardData?.content_config?.title,
      vibe_dash_slicers: slicerNames,
    });

    setShowSlackAuthModal(false);
    setToastProps({
      message: "Slack connected! Copied dashboard link to clipboard",
      type: "default",
    });
  }, [setToastProps, dashboardId, dashboardData, track]);

  const handleSlackOAuthFailure = useCallback(
    (errorMessage: string) => {
      setShowSlackAuthModal(false);
      setToastProps({
        message: errorMessage || "Failed to connect Slack",
        type: "error",
      });
    },
    [setToastProps]
  );

  const {
    isAuthorized: isSlackAuthorized,
    featureEnabled: enableSlackUnfurl,
    isCheckingStatus: isCheckingSlackStatus,
    isAuthInProgress: isSlackAuthInProgress,
    startOAuth: startSlackOAuth,
    cancelOAuth: cancelSlackOAuth,
  } = useSlackOAuth({
    userEmail: userEmail || "",
    enabled: isAuthenticated,
    onSuccess: handleSlackOAuthSuccess,
    onFailure: handleSlackOAuthFailure,
  });

  // Wrapper for startSlackOAuth that tracks the click event
  const handleSlackConnectClick = useCallback(() => {
    // Track Slack connect clicked
    const slicerNames =
      dashboardData?.content_config?.content_spec?.slicers?.map((s: { name: string }) => s.name) ||
      [];
    track(AnalyticsEventNameType.VibeDashSlackConnectClicked, {
      vibe_dash_id: dashboardId,
      ss_id: dashboardData?.spreadsheet_id,
      vibe_dash_name: dashboardData?.content_config?.title,
      vibe_dash_slicers: slicerNames,
    });

    // Start the OAuth flow
    startSlackOAuth();
  }, [dashboardId, dashboardData, track, startSlackOAuth]);

  const openLocalExcelUploadModal = useCallback(() => {
    setLocalExcelUploadError(null);
    setLocalUploadSelectedFile(null);
    setIsDragOverUpload(false);
    if (localExcelFileInputRef.current) {
      localExcelFileInputRef.current.value = "";
    }
    setIsLocalExcelUploadModalOpen(true);
  }, []);

  const handleLocalUploadChooseClick = useCallback(() => {
    if (isRefreshing || isUploadingLocalExcel) {
      return;
    }
    localExcelFileInputRef.current?.click();
  }, [isRefreshing, isUploadingLocalExcel]);

  const handleLocalUploadInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const fileName = file.name.toLowerCase();
    const isAllowed = fileName.endsWith(".xlsx") || fileName.endsWith(".xlsm");
    if (!isAllowed) {
      setLocalUploadSelectedFile(null);
      setLocalExcelUploadError("Please upload an Excel (.xlsx or .xlsm) file.");
      if (localExcelFileInputRef.current) {
        localExcelFileInputRef.current.value = "";
      }
      return;
    }
    setLocalExcelUploadError(null);
    setLocalUploadSelectedFile(file);
  }, []);

  const handleLocalUploadCancel = useCallback(() => {
    if (isUploadingLocalExcel) {
      return;
    }
    setIsDragOverUpload(false);
    setLocalUploadSelectedFile(null);
    setLocalExcelUploadError(null);
    if (localExcelFileInputRef.current) {
      localExcelFileInputRef.current.value = "";
    }
    setIsLocalExcelUploadModalOpen(false);
  }, [isUploadingLocalExcel]);

  const handleLocalUploadRefreshNow = useCallback(async () => {
    if (!localUploadSelectedFile || isUploadingLocalExcel || isRefreshing) {
      return;
    }

    if (!dashboardData?.spreadsheet_id) {
      setLocalExcelUploadError("Upload failed. Please try again.");
      return;
    }

    setLocalExcelUploadError(null);
    setIsUploadingLocalExcel(true);
    try {
      const presigned = await apiClient.getExcelSheetsContextPresignedUrl({
        get_excel_sheets_context_presigned_url_request: {
          excel_file_id: dashboardData.spreadsheet_id,
          content_length_bytes: localUploadSelectedFile.size,
        },
      });
      const data = presigned.data as any;
      const uploadType: "post" | "multipart" =
        data.upload_type === "multipart" ? "multipart" : "post";
      const uploadStartMs = performance.now();
      const uploadWorker = new Worker(new URL("./excelUploadWorker.ts", import.meta.url));
      const uploadWorkerApi = Comlink.wrap<ExcelUploadWorkerApi>(uploadWorker);
      try {
        if (data.upload_type === "multipart") {
          const parts = await uploadWorkerApi.uploadToPresignedS3Multipart(
            data.parts,
            data.part_size_bytes,
            localUploadSelectedFile
          );
          await apiClient.completeExcelSheetsContextMultipartUpload({
            complete_excel_sheets_context_multipart_upload_request: {
              s3_key: data.s3_key,
              upload_id: data.upload_id,
              parts,
            } as any,
          } as any);
        } else {
          await uploadWorkerApi.uploadToPresignedS3Post(
            data.signed_s3_url,
            data.signed_fields,
            localUploadSelectedFile,
            localUploadSelectedFile.name
          );
        }
      } finally {
        uploadWorker.terminate();
      }
      const uploadDurationMs = Math.max(0, Math.round(performance.now() - uploadStartMs));
      setIsLocalExcelUploadModalOpen(false);
      // No tabs_config: tab changes auto-save via the tabs_config endpoint and the refresh
      // task falls back to the stored config.
      handleRefreshDashboard(undefined, data.s3_key, localUploadSelectedFile.name, {
        file_size_bytes: localUploadSelectedFile.size,
        upload_type: uploadType,
        upload_duration_ms: uploadDurationMs,
        multipart_part_count:
          uploadType === "multipart" ? data.parts?.length ?? undefined : undefined,
      });
    } catch (e) {
      console.error("Local Excel upload failed:", e);
      setLocalExcelUploadError("Upload failed. Please try again.");
      setToastProps({
        message: "Upload failed. Please try again.",
        type: "error",
        duration: 5000,
        position: "bottom",
      });
    } finally {
      setIsUploadingLocalExcel(false);
      if (localExcelFileInputRef.current) {
        localExcelFileInputRef.current.value = "";
      }
    }
  }, [
    apiClient,
    dashboardData?.spreadsheet_id,
    handleRefreshDashboard,
    isRefreshing,
    isUploadingLocalExcel,
    localUploadSelectedFile,
    setToastProps,
  ]);

  // Handle clicks outside dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // If the clicked element was removed from the DOM during this same click
      // (e.g. a menu item that conditionally re-renders its own container on click),
      // it will no longer be "contained" by any ref — which would falsely read as an
      // outside click. Bail out: a detached target was inside when it was clicked.
      const target = e.target as Node | null;
      if (target && !target.isConnected) {
        return;
      }
      const clickedInsideDisableTabModal =
        (target as Element | null)?.closest?.("[data-source-sheet-disable-modal]") != null;

      // Close more menu dropdown if clicked outside
      if (
        showMoreMenuDropdown &&
        moreMenuDropdownRef.current &&
        moreMenuButtonRef.current &&
        !moreMenuDropdownRef.current.contains(e.target as Node) &&
        !moreMenuButtonRef.current.contains(e.target as Node)
      ) {
        setShowMoreMenuDropdown(false);
      }
      // Close user menu dropdown if clicked outside
      if (
        showUserMenuDropdown &&
        userMenuButtonRef.current &&
        userMenuDropdownRef.current &&
        !userMenuButtonRef.current.contains(e.target as Node) &&
        !userMenuDropdownRef.current.contains(e.target as Node)
      ) {
        setShowUserMenuDropdown(false);
      }

      // Close source sheet dropdown if clicked outside
      if (
        showSourceSheetDropdown &&
        sourceSheetButtonRef.current &&
        sourceSheetDropdownRef.current &&
        !sourceSheetButtonRef.current.contains(e.target as Node) &&
        !sourceSheetDropdownRef.current.contains(e.target as Node) &&
        !clickedInsideDisableTabModal
      ) {
        setShowSourceSheetDropdown(false);
      }

      // Close logo "My Dashboards" popover if clicked outside
      if (
        showLogoDashboardsPopover &&
        logoButtonRef.current &&
        logoDashboardsRef.current &&
        !logoButtonRef.current.contains(e.target as Node) &&
        !logoDashboardsRef.current.contains(e.target as Node)
      ) {
        setShowLogoDashboardsPopover(false);
      }

      // Close share modal if clicked outside
      if (
        showShareModal &&
        shareButtonRef.current &&
        shareModalRef.current &&
        !shareButtonRef.current.contains(e.target as Node) &&
        !shareModalRef.current.contains(e.target as Node)
      ) {
        setShowShareModal(false);
      }

      // Close subscribe modal if clicked outside
      if (
        showSubscribeModal &&
        subscribeButtonRef.current &&
        subscribeModalRef.current &&
        !subscribeButtonRef.current.contains(e.target as Node) &&
        !subscribeModalRef.current.contains(e.target as Node)
      ) {
        setShowSubscribeModal(false);
      }

      if (
        showUserMenu &&
        userMenuRef.current &&
        userMenuButtonRef.current &&
        !userMenuRef.current.contains(e.target as Node) &&
        !userMenuButtonRef.current.contains(e.target as Node)
      ) {
        setShowUserMenu(false);
      }

      // Close mobile nav overflow menu if clicked outside
      if (
        showMobileNavMenu &&
        mobileNavButtonRef.current &&
        mobileNavMenuRef.current &&
        !mobileNavButtonRef.current.contains(e.target as Node) &&
        !mobileNavMenuRef.current.contains(e.target as Node)
      ) {
        setShowMobileNavMenu(false);
      }
    };

    targetDocument.addEventListener("click", handleClickOutside);

    return () => {
      targetDocument.removeEventListener("click", handleClickOutside);
    };
  }, [
    showUserMenuDropdown,
    showSourceSheetDropdown,
    showLogoDashboardsPopover,
    showShareModal,
    showSubscribeModal,
    showUserMenu,
    showMoreMenuDropdown,
    showMobileNavMenu,
    targetDocument,
  ]);

  // Drop an .xlsx/.xlsm anywhere on the dashboard to refresh — opens the upload modal
  // with the file pre-selected. Listeners are attached to BOTH the parent document and the
  // iframe document (the dashboard body lives in a same-origin srcDoc iframe, so drops over
  // it fire inside the iframe and never reach the parent). Local Excel + editors only.
  useEffect(() => {
    if (!isLocalExcelDashboard || !canEditDashboard || isUploadingLocalExcel) {
      // Drag/drop is disabled in this state — make sure any lingering overlay is dismissed.
      setIsFileDragActive(false);
      return;
    }
    const parentDoc = window.document;
    const docs =
      targetDocument && targetDocument !== parentDoc ? [parentDoc, targetDocument] : [parentDoc];

    let hideTimer: ReturnType<typeof setTimeout> | undefined;
    const dragHasFiles = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes("Files");
    const isExcelFile = (name: string) => /\.(xlsx|xlsm)$/i.test(name);

    const onDragOver = (e: DragEvent) => {
      if (!dragHasFiles(e)) {
        return;
      }
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "copy";
      }
      setIsFileDragActive(true);
      if (hideTimer) {
        clearTimeout(hideTimer);
      }
      // dragleave/enter flicker a lot across nested nodes + two documents; clear on a short
      // idle instead so the overlay stays steady while the file is hovering.
      hideTimer = setTimeout(() => setIsFileDragActive(false), 150);
    };

    const onDrop = (e: DragEvent) => {
      if (!dragHasFiles(e)) {
        return;
      }
      e.preventDefault();
      if (hideTimer) {
        clearTimeout(hideTimer);
      }
      setIsFileDragActive(false);
      const file = e.dataTransfer?.files?.[0];
      if (!file || !isExcelFile(file.name)) {
        return;
      }
      setLocalExcelUploadError(null);
      setLocalUploadSelectedFile(file);
      setIsLocalExcelUploadModalOpen(true);
    };

    docs.forEach(d => {
      d.addEventListener("dragover", onDragOver);
      d.addEventListener("drop", onDrop);
    });
    return () => {
      if (hideTimer) {
        clearTimeout(hideTimer);
      }
      docs.forEach(d => {
        d.removeEventListener("dragover", onDragOver);
        d.removeEventListener("drop", onDrop);
      });
      // The idle-hide timer may have been pending when we tore down — reset so a dependency
      // change mid-drag can't leave the overlay stuck visible with no listener to clear it.
      setIsFileDragActive(false);
    };
  }, [isLocalExcelDashboard, canEditDashboard, isUploadingLocalExcel, targetDocument]);

  // Handle scroll to add .nav-stickied class when page is scrolled past the header
  useEffect(() => {
    const targetWindow = targetDocument.defaultView;
    if (!targetWindow) {
      return;
    }

    const handleScroll = () => {
      const headerElem = targetDocument.getElementById("mainHeader");
      const navBarElem = targetDocument.getElementById("navBar");
      if (!headerElem || !navBarElem) {
        return;
      }

      // Use the header's viewport-relative top (already scroll-adjusted, independent of its
      // #dashboardMainContainer offsetParent). offsetTop would be measured relative to that
      // positioned ancestor (~the header's top margin), never reaching the navbar-height
      // threshold, which left the navbar stuck "stickied" — and the dashboard title stuck
      // visible — even after scrolling back to the top.
      const headerTopInViewport = headerElem.getBoundingClientRect().top;

      if (headerTopInViewport < navBarElem.offsetHeight) {
        navBarElem.classList.add("nav-stickied");
      } else {
        navBarElem.classList.remove("nav-stickied");
      }
    };

    targetWindow.addEventListener("scroll", handleScroll);
    // Set the correct initial state without waiting for the first scroll event.
    handleScroll();

    return () => targetWindow.removeEventListener("scroll", handleScroll);
  }, [targetDocument]);

  const handleSourceSheetToggle = () => {
    setShowSourceSheetDropdown(!showSourceSheetDropdown);
  };

  // Handle open sheet click with tracking
  const handleOpenSheet = useCallback(() => {
    if (!spreadsheetId) {
      return;
    }

    const slicerNames =
      dashboardData?.content_config?.content_spec?.slicers?.map((s: { name: string }) => s.name) ||
      [];
    track(AnalyticsEventNameType.VibeDashOpenSheetClicked, {
      vibe_dash_id: dashboardId,
      ss_id: spreadsheetId,
      vibe_dash_name: dashboardData?.content_config?.title,
      vibe_dash_slicers: slicerNames,
    });

    const isExcel = (dashboardData as any)?.source_type === "excel";
    const excelUrl = (dashboardData as any)?.source_workbook_url as string | undefined;
    if (isExcel) {
      if (excelUrl) {
        window.open(excelUrl, "_blank");
      } else {
        setToastProps({
          message:
            "Source workbook link isn’t ready yet. Please refresh the dashboard, then try again.",
          type: "error",
        });
      }
      return;
    }

    window.open(`https://docs.google.com/spreadsheets/d/${spreadsheetId}`, "_blank");
  }, [dashboardId, dashboardData, spreadsheetId, track, setToastProps]);

  // Handle share button click
  // Uses trackNavBarEvent to support both authenticated and anonymous users
  const handleShare = useCallback(async () => {
    const slicerNames =
      dashboardData?.content_config?.content_spec?.slicers?.map(s => s.name) || [];
    // Track share clicked
    if (dashboardId && dashboardData) {
      trackNavBarEvent(AnalyticsEventNameType.VibeDashShareClicked, {
        vibe_dash_id: dashboardId,
        ss_id: spreadsheetId || dashboardData.spreadsheet_id,
        vibe_dash_name: dashboardData.content_config?.title,
        vibe_dash_slicers: slicerNames,
      });
    }

    if (isAuthenticated) {
      setShowShareModal(true);
      return;
    }

    const dashboardLink = new URL(`/dashboards/${dashboardId}`, window.location.origin);
    try {
      await navigator.clipboard.writeText(dashboardLink.toString());
    } catch {
      setToastProps({
        message: "Failed to copy dashboard link",
        type: "error",
      });
      return;
    }

    // If still checking auth status, just show copied toast (check this FIRST)
    // Don't mention Slack until we know if feature is enabled
    if (isCheckingSlackStatus) {
      setToastProps({
        message: "Copied dashboard link to clipboard",
        type: "default",
      });
      return;
    }

    // If feature flag is disabled, just show copied toast (no Slack integration)
    if (!enableSlackUnfurl) {
      setToastProps({
        message: "Copied dashboard link to clipboard",
        type: "default",
      });
      return;
    }

    // Auth check complete - show appropriate UI immediately
    if (isSlackAuthorized) {
      setToastProps({
        message: "Copied dashboard link to clipboard",
        type: "default",
      });
    } else {
      setShowSlackAuthModal(true);
    }
  }, [
    dashboardId,
    isAuthenticated,
    enableSlackUnfurl,
    isCheckingSlackStatus,
    isSlackAuthorized,
    setToastProps,
    dashboardData,
    spreadsheetId,
    trackNavBarEvent,
  ]);

  // For dashboard title (shown when nav bar is stickied) and last refresh timestamp
  const navBarTextElementClasses = "hidden text-xs 2xl:text-sm transition duration-200";

  const isNavPopoverBackdropOpen =
    showSourceSheetDropdown ||
    showShareModal ||
    showSubscribeModal ||
    showUserMenuDropdown ||
    Boolean(isThemeSelectorOpen);

  // The theme selector's whole point is live-previewing colors, so its backdrop must not dim the
  // dashboard — keep the click-to-close layer but drop the tint when only the selector is open.
  const isBackdropTinted =
    showSourceSheetDropdown || showShareModal || showSubscribeModal || showUserMenuDropdown;

  const closeNavPopoverBackdrop = useCallback(() => {
    setShowSourceSheetDropdown(false);
    setShowShareModal(false);
    setShowSubscribeModal(false);
    setShowUserMenuDropdown(false);
    setIsThemeSelectorOpen?.(false);
  }, [setIsThemeSelectorOpen]);

  return (
    <>
      {isFileDragActive && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-primary-button-background/10 pointer-events-none"
          aria-hidden
        >
          <div className="flex items-center gap-3 rounded-xl border-2 border-dashed border-primary-button-background bg-main-background px-7 py-5 text-base font-semibold text-main-foreground shadow-2xl">
            <ExcelSheetsIcon width={28} height={28} />
            Drop your Excel file to refresh
          </div>
        </div>
      )}
      {isNavPopoverBackdropOpen && (
        <div
          className={"fixed inset-0 z-[199]" + (isBackdropTinted ? " bg-black/20" : "")}
          aria-hidden
          onClick={closeNavPopoverBackdrop}
        />
      )}
      <nav
        id="navBar"
        className={
          "group/nav h-16 flex border-b px-3 sm:px-6 py-3 sticky top-0 z-200 relative " +
          // nav bar starts off transparent until the dashboard title is scrolled out of view
          "bg-transparent border-transparent text-nav-foreground duration-200 " +
          "[&.nav-stickied]:bg-nav-background [&.nav-stickied]:shadow-nav [&.nav-stickied]:border-nav-border [&.nav-stickied]:backdrop-blur-xs"
        }
      >
        <div className="flex items-center gap-3 justify-between w-full">
          {/* Left side of nav bar */}
          <div className="flex items-center gap-5 grow">
            <div className="relative">
              <button
                ref={logoButtonRef}
                type="button"
                className={`flex items-center ${isAuthenticated ? "cursor-pointer" : ""}`}
                onClick={() => {
                  if (!isAuthenticated) {
                    return;
                  }
                  setShowLogoDashboardsPopover(v => !v);
                }}
                title={isAuthenticated ? "My Dashboards" : undefined}
              >
                {activeCustomLogo ? (
                  <img
                    src={activeCustomLogo.src}
                    alt={activeCustomLogo.alt}
                    style={{ height: activeCustomLogo.height || 32 }}
                    className="object-contain min-w-[80px]"
                  />
                ) : (
                  <div className="min-w-[80px] max-w-[128px] transition">
                    {currentTheme.logoVariant === "dark" ? (
                      <CoeLogo width="100%" height="100%" />
                    ) : currentTheme.logoVariant === "light_text" ? (
                      <CoeLogoWhiteText width="100%" height="100%" />
                    ) : (
                      <CoeLogoWhiteLogoAndText width="100%" height="100%" />
                    )}
                  </div>
                )}
              </button>

              {/* Width matches the popover (420px) so its built-in right-0 left-aligns
                  it to the logo; top-full puts it just below the logo. On mobile the popover
                  renders as a portaled bottom sheet (escaping this wrapper), so collapse the
                  width to 0 — a 420px absolute box would otherwise overflow the phone viewport
                  and scroll the page horizontally (Safari counts the zero-height box in
                  scrollWidth). */}
              <div className="absolute left-0 top-full w-[420px] max-sm:w-0">
                <MyDashboardsPopover
                  visible={showLogoDashboardsPopover && isAuthenticated}
                  dashboards={dashboards}
                  isLoading={isLoadingDashboards}
                  dropdownRef={logoDashboardsRef}
                  onClose={() => setShowLogoDashboardsPopover(false)}
                  targetDocument={targetDocument}
                />
              </div>
            </div>

            {/* Super admin view badge with countdown */}
            {hasSuperAdminAccess && superAdminTimeRemaining && (
              <div
                className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300"
                title="Internal super admin view - read only"
              >
                <span className="material-icons !text-sm">admin_panel_settings</span>
                <span>Admin View</span>
                <span className="font-mono">{superAdminTimeRemaining}</span>
              </div>
            )}

            {/* "Powered by Coefficient" text takes the same location as the sticky nav dashboard title.
                Only shown with the default Coefficient logo; hidden when a custom logo is in use. */}
            <div className="inline-block relative grow">
              <span
                id="navTitle"
                className={
                  `${navBarTextElementClasses} ${
                    isChatSidebarOpen ? "xl:block" : "lg:block"
                  } max-md:hidden ` +
                  "absolute top-0 -translate-y-1/2 " +
                  "font-semibold text-nav-foreground opacity-0 " +
                  "group-[.nav-stickied]/nav:opacity-100"
                }
              >
                {dashboardTitle}
              </span>
            </div>
          </div>

          {/* Right side of nav bar. When archived the whole action row (My Dashboards, Source
              Sheet, Subscribe, Share, More, Ask/Edit) is greyed + inert. */}
          <div
            className={
              "flex items-center gap-0 xl:gap-1" +
              (isArchived ? " opacity-40 pointer-events-none" : "")
            }
          >
            {/* Trial countdown / expired badge — show to owner even on public dashboards. Not in
                the mobile design; hidden below the breakpoint rather than cramming it in. */}
            {canEditDashboard && trialDaysRemaining !== null && (
              <div className="max-sm:hidden flex items-center gap-2 rounded-full bg-[#eef1ff] px-3 py-1.5 text-[13px] font-medium text-[#111215]">
                <span className="material-icons !text-[16px] text-[#3053ff]">error</span>
                <span>
                  {trialDaysRemaining} day{trialDaysRemaining !== 1 ? "s" : ""} left in your trial
                </span>
                <span className="h-3.5 w-px bg-[#c7cfe8]" />
                <button
                  onClick={onProactiveUpgradeClick}
                  className="font-semibold text-[#3053ff]"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  Upgrade
                </button>
              </div>
            )}

            {/* Source Sheet dropdown - combines Open in Sheets, Refresh, and last refreshed timestamp.
                Below 640px this button is hidden and "Source Sheet" instead lives in the mobile
                nav overflow menu (below), which calls handleSourceSheetToggle directly — the
                wrapper div here stays mounted (not hidden) so SourceSheetModal, which positions
                itself via `absolute top-full right-0` relative to this div, still has a home to
                render into when opened that way. */}
            {spreadsheetId && (
              <div className="relative">
                <button
                  ref={sourceSheetButtonRef}
                  onClick={handleSourceSheetToggle}
                  className={`max-sm:hidden ${WEB_DASH_NAV_TEXT_ACTION_CLASS}  ${
                    isDashboardBuilding ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  disabled={isDashboardBuilding}
                  title={
                    isDashboardBuilding
                      ? "Source sheet actions are available after the dashboard finishes building"
                      : isLocalExcelDashboard
                      ? "Refresh"
                      : "Source Sheet"
                  }
                >
                  <WebDashboardNavIconSourceSheet />
                  <span className={navLabelClass(isChatSidebarOpen)}>
                    {isLocalExcelDashboard ? "Refresh" : "Source Sheet"}
                  </span>
                </button>

                {showSourceSheetDropdown && (
                  <SourceSheetModal
                    dashboardData={dashboardData}
                    isEditingEnabled={isEditingEnabled}
                    isRefreshing={isRefreshing}
                    isCheckingChanges={isCheckingChanges}
                    trialExpired={trialExpired}
                    isUploadingLocalExcel={isUploadingLocalExcel}
                    handleRefreshDashboard={handleRefreshDashboard}
                    handleOpenSheet={handleOpenSheet}
                    onClose={() => setShowSourceSheetDropdown(false)}
                    portalDocument={targetDocument}
                    onOpenLocalExcelUploadModal={openLocalExcelUploadModal}
                    onDeleteBlocks={onDeleteBlocks}
                    rootRef={sourceSheetDropdownRef}
                  />
                )}
              </div>
            )}

            {/* Editor-only action; excluded from the mobile viewer-only nav entirely (no mobile
                equivalent for this pass). */}
            <div className="relative max-sm:hidden">
              <button
                ref={subscribeButtonRef}
                type="button"
                className={`${WEB_DASH_NAV_TEXT_ACTION_CLASS} ${
                  isDashboardBuilding ? "opacity-60 cursor-not-allowed" : ""
                }`}
                onClick={handleSubscribeClick}
                disabled={isDashboardBuilding}
                title={
                  isDashboardBuilding
                    ? "Subscribe is available after the dashboard finishes building"
                    : "Subscribe"
                }
                aria-expanded={showSubscribeModal}
              >
                <WebDashboardNavIconSubscribe />
                <span className={navLabelClass(isChatSidebarOpen)}>Subscribe</span>
              </button>

              <SubscribeDashboardModal
                ref={subscribeModalRef}
                open={showSubscribeModal}
                onClose={() => setShowSubscribeModal(false)}
                dashboardId={dashboardId}
                blocks={(dashboardData?.content_config?.content_spec?.blocks as any[]) ?? []}
                userEmail={userEmail}
                targetDocument={targetDocument}
              />
            </div>

            {/* Below 640px this button is hidden and "Share" instead lives in the mobile nav
                overflow menu (below), which calls handleShare directly — the wrapper div stays
                mounted so ShareDashboardModal (absolute top-full right-0 relative to it) still
                has a home to render into. */}
            <div className="relative">
              <button
                ref={shareButtonRef}
                className={`max-sm:hidden ${WEB_DASH_NAV_TEXT_ACTION_CLASS} ${
                  isDashboardBuilding ? "opacity-60 cursor-not-allowed" : ""
                }`}
                onClick={handleShare}
                disabled={isDashboardBuilding}
                title={
                  isDashboardBuilding
                    ? "Sharing is available after the dashboard finishes building"
                    : "Share"
                }
              >
                <WebDashboardNavIconShare />
                <span className={navLabelClass(isChatSidebarOpen)}>Share</span>
              </button>

              {dashboardId && (
                <ShareDashboardModal
                  ref={shareModalRef}
                  dashboardId={dashboardId}
                  open={showShareModal}
                  onClose={() => setShowShareModal(false)}
                  requestSuperAdminView={requestSuperAdminView}
                  hasPersonalizedViews={hasPersonalizedViews}
                  onOpenPersonalizedViews={enableViewAsFilter ? onOpenPersonalizedViews : undefined}
                  targetDocument={targetDocument}
                />
              )}
            </div>

            {/* Theme/Rename/Archive are editor-only actions, excluded from the mobile
                viewer-only nav entirely for this pass. */}
            <div className="max-sm:hidden">
              <NavBarMoreMenu
                isOwner={isOwner}
                canEditDashboard={canEditDashboard}
                // Any entitled authenticated viewer can make their own same-data copy.
                canDuplicate={isAuthenticated && !isCurrentUserWebDashboardTrialExpired}
                isOpen={showMoreMenuDropdown}
                isArchived={isArchived}
                isDashboardBuilding={isDashboardBuilding}
                isEditingEnabled={isEditingEnabled}
                onNavButtonClick={() => {
                  setShowMoreMenuDropdown(isOpen => !isOpen);
                }}
                dropdownRef={moreMenuDropdownRef}
                buttonRef={moreMenuButtonRef}
                onThemesClick={() => {
                  setIsThemeSelectorOpen(true);
                  setShowMoreMenuDropdown(false);
                }}
                onRenameClick={() => setShowMoreMenuDropdown(false)}
                onArchiveClick={() => setShowMoreMenuDropdown(false)}
                onDuplicateClick={() => setShowMoreMenuDropdown(false)}
                onArchived={() => setJustArchived(true)}
                // ThemeSelector Props
                handleThemeChange={handleThemeChange}
                targetDocument={targetDocument}
                themeId={themeId}
                mode={mode}
                background={background}
                isThemeSelectorOpen={isThemeSelectorOpen}
                setIsThemeSelectorOpen={setIsThemeSelectorOpen}
                onPreviewTheme={onPreviewTheme}
                onThemeCommitted={onThemeCommitted}
                onThemeUncommitted={onThemeUncommitted}
                onThemeDeleted={onThemeDeleted}
                onThemeDeleteFailed={onThemeDeleteFailed}
                onRequestTheme={onRequestTheme}
                onEditTheme={onEditTheme}
              />
            </div>

            {/* AI entrypoint: "Editor Thread" for super admin, "Ask" in viewer mode, "Edit" in editor mode */}
            {/* For public dashboards with unauthenticated users, Ask button is disabled */}
            {/* Ask button is disabled for non-editors when Personalized Views is active */}
            {/* Chat/edit is an editor-oriented capability with no mobile design yet this pass —
                hidden below 640px entirely rather than shipping an untested mobile chat layout. */}
            <div className="max-sm:hidden">
              {dashboardMode === "viewer" || hasSuperAdminAccess || isDashboardBuilding ? (
                <div
                  className="relative inline-flex"
                  onMouseEnter={() => {
                    setIsAskButtonHovered(true);
                    if (hasPersonalizedViews && !canEditDashboard && !hasSuperAdminAccess) {
                      setShowAskDisabledTooltip(true);
                    }
                  }}
                  onMouseLeave={() => {
                    setIsAskButtonHovered(false);
                    setShowAskDisabledTooltip(false);
                  }}
                >
                  {/* Login prompt shown to unauthenticated viewers when the Ask button is disabled.
                    Displays to the left; the native title is suppressed for this case to avoid a
                    duplicate browser tooltip. */}
                  <Tooltip
                    direction={TooltipDirection.Left}
                    showTooltip={isAskButtonHovered && !isAuthenticated && isAskButtonDisabled}
                  >
                    Log in to chat with Coefficient AI
                  </Tooltip>
                  <button
                    type="button"
                    className={`ml-1 inline-flex h-8 items-center gap-1.5 rounded-[12px] pl-2 pr-4 text-white shadow-sm transition-colors ${
                      isAskButtonDisabled
                        ? "bg-gray-400 cursor-not-allowed"
                        : "cursor-pointer bg-[#3053ff] hover:bg-blue-700"
                    }`}
                    onClick={handleEditButtonClick}
                    disabled={isAskButtonDisabled}
                    title={
                      // Unauthenticated case is handled by the custom Tooltip above (no native
                      // title, to avoid a duplicate tooltip).
                      isDashboardBuilding
                        ? "AI chat is available after the dashboard finishes building"
                        : hasSuperAdminAccess
                        ? "View editor thread history"
                        : undefined
                    }
                  >
                    {isDashboardBuilding ? (
                      <span className="material-icons !text-xl" aria-hidden>
                        hourglass_top
                      </span>
                    ) : hasSuperAdminAccess ? (
                      <span className="material-icons !text-xl" aria-hidden>
                        history
                      </span>
                    ) : (
                      <WebDashboardNavIconAiSparkle />
                    )}
                    <span className="text-[13px] font-semibold leading-[18px]">
                      {isDashboardBuilding
                        ? "Building"
                        : hasSuperAdminAccess
                        ? "Editor Thread"
                        : "Ask"}
                    </span>
                  </button>
                  {/* Personalized Views notice — only for authenticated viewers, so it never
                    collides with the unauthenticated login tooltip above. */}
                  {showAskDisabledTooltip && isAuthenticated && (
                    <div className="absolute top-full right-0 mt-2 w-56 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 z-50 pointer-events-none text-center">
                      AI chat is disabled for viewers on dashboards with Personalized Views
                    </div>
                  )}
                </div>
              ) : (
                isEditingEnabled &&
                !isChatSidebarOpen && (
                  <GradientAIButton
                    label="Edit"
                    leadingIcon={<WebDashboardNavIconAiSparkle />}
                    onClick={handleEditButtonClick}
                    mode={mode}
                  />
                )
              )}
            </div>

            {/* Below 640px, log in / user menu are reachable via the mobile nav overflow menu
                (below) instead of showing inline — there's no room for both a condensed header
                and these buttons on a phone width. */}
            {!isAuthenticated && (
              <div className="max-sm:hidden">
                <NavBarLoginButton />
              </div>
            )}

            {(name || userEmail) && isAuthenticated && (
              <div className="relative ml-[10px] max-sm:hidden" ref={userMenuButtonRef}>
                <UserIcon name={name} email={userEmail} onClick={() => setShowUserMenu(v => !v)} />
                {showUserMenu && (
                  <div ref={userMenuRef} className="absolute right-0 top-full mt-2 z-[210]">
                    <UserMenuDropdown
                      name={name}
                      email={userEmail ?? ""}
                      isAuthenticated={isAuthenticated}
                      showHelpExtraOptions={dashboardMode !== "viewer" || isDashboardBuilding}
                      onSupportFeedbackClick={() => {
                        setShowUserMenu(false);
                        setShowSupportFeedbackModal(true);
                      }}
                      onHelpVideoClick={() => {
                        setShowUserMenu(false);
                        setShowHelpVideoModal(true);
                      }}
                      onMemoryClick={() => {
                        setShowUserMenu(false);
                        setShowMemoryModal(true);
                      }}
                      onLogoutClick={() => {
                        setShowUserMenu(false);
                        handleSignOut();
                      }}
                      onClose={() => setShowUserMenu(false)}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Mobile (<640px) nav overflow trigger. Desktop/tablet unaffected — sm:hidden. */}
            <div className="relative sm:hidden">
              <button
                ref={mobileNavButtonRef}
                type="button"
                className="themed-nav-button px-3"
                onClick={() => setShowMobileNavMenu(v => !v)}
                aria-expanded={showMobileNavMenu}
                aria-haspopup="menu"
                title="Menu"
              >
                <HamburgerMenuIcon />
              </button>

              {showMobileNavMenu && (
                <div
                  ref={mobileNavMenuRef}
                  className="themed-dropdown-menu flex flex-col gap-3 p-5"
                  role="menu"
                >
                  {/* Action items, separated from the user block by a divider. */}
                  <div className="flex flex-col gap-2.5 border-b border-main-border pb-3">
                    {spreadsheetId && (
                      <button
                        type="button"
                        role="menuitem"
                        className={`flex gap-1.5 items-center h-6 w-full text-left text-sm text-main-foreground leading-[18px] cursor-pointer transition-colors hover:text-main-text-button-hover-foreground ${
                          isDashboardBuilding ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isDashboardBuilding}
                        onClick={() => {
                          setShowMobileNavMenu(false);
                          handleSourceSheetToggle();
                        }}
                      >
                        <span className="flex items-center justify-center size-6 shrink-0">
                          <WebDashboardNavIconSourceSheet />
                        </span>
                        {isLocalExcelDashboard ? "Refresh" : "Source Sheet"}
                      </button>
                    )}

                    <button
                      type="button"
                      role="menuitem"
                      className={`flex gap-1.5 items-center h-6 w-full text-left text-sm text-main-foreground leading-[18px] cursor-pointer transition-colors hover:text-main-text-button-hover-foreground ${
                        isDashboardBuilding ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                      disabled={isDashboardBuilding}
                      onClick={() => {
                        setShowMobileNavMenu(false);
                        handleShare();
                      }}
                    >
                      <span className="flex items-center justify-center size-6 shrink-0">
                        <WebDashboardNavIconShare />
                      </span>
                      Share
                    </button>

                    {isAuthenticated &&
                      !isLoadingDashboards &&
                      dashboards &&
                      dashboards.length >= 1 && (
                        <button
                          type="button"
                          role="menuitem"
                          className="flex gap-1.5 items-center h-6 w-full text-left text-sm text-main-foreground leading-[18px] cursor-pointer transition-colors hover:text-main-text-button-hover-foreground"
                          onClick={() => {
                            setShowMobileNavMenu(false);
                            setShowLogoDashboardsPopover(true);
                          }}
                        >
                          <span className="flex items-center justify-center size-6 shrink-0">
                            <WebDashboardNavIconMyDashboards />
                          </span>
                          My Dashboards
                        </button>
                      )}
                  </div>

                  {isAuthenticated ? (
                    <div className="flex gap-2.5 items-start">
                      <UserIcon name={name} email={userEmail} />
                      <div className="flex flex-col gap-1 min-w-0">
                        <span className="text-sm text-main-foreground leading-[18px] truncate">
                          {name}
                        </span>
                        <span className="text-xs text-main-muted-foreground leading-[18px] truncate">
                          {userEmail}
                        </span>
                        <button
                          type="button"
                          role="menuitem"
                          className="text-sm text-main-foreground leading-[18px] text-left w-fit cursor-pointer transition-colors hover:text-main-text-button-hover-foreground"
                          onClick={() => {
                            setShowMobileNavMenu(false);
                            handleSignOut();
                          }}
                        >
                          Log out
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div onClick={() => setShowMobileNavMenu(false)}>
                      <NavBarLoginButton />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Spacer for right sidebars. Takes up space when a sidebar is open so
      none of the navbar content gets obscured. */}
        <ChatSidebarFlexSpacer isChatSidebarOpen={isChatSidebarOpen} width={chatSidebarWidth} />
      </nav>

      {/* Archived banner — sticky under the nav. Read-only notice; the owner can restore inline,
          non-owners are pointed at the owner. Refresh / AI chat stay disabled while archived.
          Width matches the dashboard content (max-w-9xl mx-auto px-6). */}
      {isArchived && (
        <div className="sticky top-16 z-[199] flex">
          {/* Reserve space for the open chat sidebar (same flex-spacer pattern as the nav) so the
              banner — and its Restore button — stay clear of the fixed sidebar. */}
          <div className="grow min-w-0">
            <div className="max-w-9xl mx-auto px-6">
              <div className="flex items-center gap-4 rounded-lg bg-[#ffe1de] px-5 py-3.5 text-[13px] text-[#111215]">
                <div className="flex items-center gap-2 shrink-0">
                  <span className="material-icons !text-base text-[#de1135]">error</span>
                  <span className="font-semibold">Dashboard Archived</span>
                </div>
                <div className="grow min-w-0">
                  {isOwner ? (
                    "Restore this dashboard to resume refreshing and editing"
                  ) : (
                    <span>
                      Contact the owner{ownerEmail ? ` (${ownerEmail})` : ""} to restore this
                      dashboard
                    </span>
                  )}
                </div>
                {isOwner &&
                  (justArchived ? (
                    // First-time state: reverse the archive (free) or delete outright.
                    <div className="shrink-0 flex items-center gap-2">
                      <button
                        onClick={handleUndoArchive}
                        disabled={archiveMutation.isLoading}
                        className="font-medium text-[#3053ff] disabled:opacity-50"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "0 8px",
                        }}
                      >
                        Undo
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="font-medium text-[#3053ff]"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "0 8px",
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleRestoreClick}
                      disabled={archiveMutation.isLoading}
                      className="shrink-0 font-medium text-[#3053ff] disabled:opacity-50"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "0 8px",
                      }}
                    >
                      {archiveMutation.isLoading ? "Restoring…" : "Restore"}
                    </button>
                  ))}
              </div>
            </div>
          </div>
          <ChatSidebarFlexSpacer isChatSidebarOpen={isChatSidebarOpen} width={chatSidebarWidth} />
        </div>
      )}

      {/* Restore-limit warning — last restore. Confirm before consuming it. */}
      <DashboardModal
        open={showRestoreWarning}
        onCancel={() => setShowRestoreWarning(false)}
        closable
        width={480}
        zIndex={1200}
        getContainer={() => document.body}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ExclamationCircleFilled style={{ color: "#F8B000", fontSize: 16 }} />
            <DashboardModalTitle>Restore Limit Warning</DashboardModalTitle>
          </div>
          <div style={{ fontSize: 14, color: "#4B5563" }}>
            A dashboard can only be restored 5 times. You can restore this dashboard one more time.
            After that, you&apos;ll need to contact Support to restore.
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <button
              onClick={() => setShowRestoreWarning(false)}
              style={{
                height: 32,
                padding: "0 12px",
                borderRadius: 4,
                border: "none",
                background: "none",
                color: "#3053ff",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShowRestoreWarning(false);
                void handleRestoreDashboard();
              }}
              style={{
                height: 32,
                padding: "0 12px",
                borderRadius: 4,
                border: "none",
                background: "#3053ff",
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Proceed
            </button>
          </div>
        </div>
      </DashboardModal>

      {/* Restore-limit locked — restores exhausted, only Support can restore. */}
      <DashboardModal
        open={showRestoreLocked}
        onCancel={() => setShowRestoreLocked(false)}
        closable
        width={480}
        zIndex={1200}
        getContainer={() => document.body}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ExclamationCircleFilled style={{ color: "#F5222D", fontSize: 16 }} />
            <DashboardModalTitle>Restore limit reached</DashboardModalTitle>
          </div>
          <div style={{ fontSize: 14, color: "#4B5563" }}>
            This dashboard has already been restored 5 times and can&apos;t be restored again.
            Contact support if you need assistance.
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              onClick={() => setShowRestoreLocked(false)}
              style={{
                height: 32,
                padding: "0 12px",
                borderRadius: 4,
                border: "none",
                background: "#3053ff",
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Got it
            </button>
          </div>
        </div>
      </DashboardModal>

      {/* Plan limit reached — restoring would exceed the family's dashboard plan limit. */}
      <DashboardModal
        open={showRestorePlanLimit}
        onCancel={() => setShowRestorePlanLimit(false)}
        closable
        width={480}
        zIndex={1200}
        getContainer={() => document.body}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {planLimitSalesRequestSent ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <CheckCircleFilled style={{ color: "#22C55E", fontSize: 16 }} />
                <DashboardModalTitle>Request sent</DashboardModalTitle>
              </div>
              <div style={{ fontSize: 14, color: "#4B5563" }}>
                Our sales team will reach out with more information about upgrading your plan.
              </div>
            </>
          ) : (
            <>
              <DashboardModalTitle>Dashboard limit reached</DashboardModalTitle>
              {planLimitUsage && (
                <PlanLimitUsageBar used={planLimitUsage.used} limit={planLimitUsage.limit} />
              )}
              <div style={{ fontSize: 14, color: "#4B5563" }}>
                {isPlanManager
                  ? "You've reached your plan's dashboard limit. You can archive a dashboard or upgrade your plan to create another dashboard."
                  : `You've reached your plan's dashboard limit. Archive a dashboard to free up space, or contact your admin${
                      accountAdminEmail ? ` (${accountAdminEmail})` : ""
                    } if you need additional dashboards.`}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={
                    isPlanManager
                      ? handlePlanLimitContactSales
                      : () => setShowRestorePlanLimit(false)
                  }
                  disabled={isPlanManager && planLimitSalesSubmitting}
                  style={{
                    height: 32,
                    padding: "0 12px",
                    borderRadius: 4,
                    border: "none",
                    background: "#3053ff",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {isPlanManager
                    ? planLimitSalesSubmitting
                      ? "Sending..."
                      : "Contact Sales"
                    : "Got it"}
                </button>
              </div>
            </>
          )}
        </div>
      </DashboardModal>

      {/* Delete confirmation — first-time archived banner "Delete" action. */}
      <DeleteDashboardModal
        visible={showDeleteConfirm}
        isLoading={deleteMutation.isLoading}
        onConfirm={handleDeleteDashboard}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <DashboardSupportFeedbackModal
        open={showSupportFeedbackModal}
        onClose={() => setShowSupportFeedbackModal(false)}
        portalDocument={targetDocument}
        onSubmit={handleSupportFeedbackSubmit}
        onValidationError={message => {
          setToastProps({
            message,
            type: "error",
          });
        }}
      />

      <DashboardHelpVideoModal
        open={showHelpVideoModal}
        onClose={() => setShowHelpVideoModal(false)}
        portalDocument={targetDocument}
        videoUrl={getAiDashboardsHelpVideoUrl((dashboardData as any)?.source_type ?? null)}
      />

      {/* Slack OAuth — render outside <nav> so Ant Modal / mask are not affected by nav stacking,
          sticky/filter contexts, or overflow; portal target is still iframe document.body */}
      <SlackAuthModal
        portalDocument={targetDocument}
        open={showSlackAuthModal}
        onAuthorize={handleSlackConnectClick}
        onSkip={() => {
          cancelSlackOAuth(); // Reset OAuth state to prevent stuck loading
          setShowSlackAuthModal(false);
          setToastProps({
            message: "Copied dashboard link to clipboard",
            type: "default",
          });
        }}
        isAuthorizing={isSlackAuthInProgress}
      />

      {isUploadingLocalExcel && createPortal(<UploadingSnackbar />, document.body)}

      {/* Local Excel upload modal — render outside dropdown so it can't unmount */}
      <Modal
        open={isLocalExcelUploadModalOpen}
        onCancel={handleLocalUploadCancel}
        closable
        width={640}
        zIndex={1200}
        // Render into the main app document so AntD modal styles/mask positioning work correctly.
        // (The dashboard content may live in an iframe; `targetDocument` is used for click handling,
        // but modals should mount into the document where AntD CSS is loaded.)
        getContainer={() => document.body}
        title={
          <div style={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
            Upload Excel file to refresh
          </div>
        }
      >
        {/* This modal renders in the main app document (no Tailwind). Use explicit styles. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ fontSize: 14, color: "#4B5563" }}>
            Make sure to upload the same Excel file “
            {dashboardData?.spreadsheet_name ?? "this workbook"}”
          </div>

          <input
            ref={localExcelFileInputRef}
            type="file"
            accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
            style={{ display: "none" }}
            onChange={handleLocalUploadInputChange}
          />

          <div
            style={{
              width: "100%",
              borderRadius: 8,
              borderWidth: 1,
              borderStyle: "dashed",
              borderColor: isDragOverUpload ? "#60A5FA" : "#D1D5DB",
              background: isDragOverUpload ? "rgba(59, 130, 246, 0.06)" : "#FFFFFF",
              padding: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              userSelect: "none",
              cursor: isUploadingLocalExcel || isRefreshing ? "not-allowed" : "default",
            }}
            onDragEnter={e => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragOverUpload(true);
            }}
            onDragOver={e => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragOverUpload(true);
            }}
            onDragLeave={e => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragOverUpload(false);
            }}
            onDrop={e => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragOverUpload(false);
              const file = e.dataTransfer.files?.[0];
              if (file) {
                const fileName = file.name.toLowerCase();
                const isAllowed = fileName.endsWith(".xlsx") || fileName.endsWith(".xlsm");
                if (!isAllowed) {
                  setLocalUploadSelectedFile(null);
                  setLocalExcelUploadError("Please upload an Excel (.xlsx or .xlsm) file.");
                  return;
                }
                setLocalExcelUploadError(null);
                setLocalUploadSelectedFile(file);
              }
            }}
          >
            <ExcelSheetsIcon width={24} height={24} />
            <div style={{ fontSize: 14, color: "#374151" }}>
              Drag and drop, or{" "}
              <button
                type="button"
                onClick={handleLocalUploadChooseClick}
                disabled={isUploadingLocalExcel || isRefreshing}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  margin: 0,
                  color: "#3053FF",
                  fontWeight: 600,
                  cursor: isUploadingLocalExcel || isRefreshing ? "not-allowed" : "pointer",
                  textDecoration: "none",
                }}
                onMouseEnter={e => {
                  if (isUploadingLocalExcel || isRefreshing) {
                    return;
                  }
                  (e.currentTarget as HTMLButtonElement).style.textDecoration = "underline";
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.textDecoration = "none";
                }}
              >
                choose Excel file
              </button>
            </div>
          </div>

          {localUploadSelectedFile && (
            <div style={{ fontSize: 14, color: "#4B5563" }}>
              Selected:{" "}
              <span className="font-medium text-gray-900">{localUploadSelectedFile.name}</span>
            </div>
          )}

          {localExcelUploadError && (
            <div style={{ fontSize: 14, color: "#DC2626" }}>{localExcelUploadError}</div>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingTop: 8,
            }}
          >
            <button
              type="button"
              onClick={handleLocalUploadCancel}
              disabled={isUploadingLocalExcel}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                margin: 0,
                color: "#3053FF",
                fontSize: 14,
                fontWeight: 500,
                cursor: isUploadingLocalExcel ? "not-allowed" : "pointer",
                textDecoration: "none",
              }}
              onMouseEnter={e => {
                if (isUploadingLocalExcel) {
                  return;
                }
                (e.currentTarget as HTMLButtonElement).style.textDecoration = "underline";
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.textDecoration = "none";
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={() => void handleLocalUploadRefreshNow()}
              disabled={
                !localUploadSelectedFile ||
                isUploadingLocalExcel ||
                isRefreshing ||
                !isEditingEnabled
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                height: 32,
                padding: "0 16px",
                borderRadius: 8,
                border: "none",
                backgroundColor: "#3053FF",
                color: "#FFFFFF",
                fontSize: 14,
                fontWeight: 600,
                cursor:
                  !localUploadSelectedFile ||
                  isUploadingLocalExcel ||
                  isRefreshing ||
                  !isEditingEnabled
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  !localUploadSelectedFile ||
                  isUploadingLocalExcel ||
                  isRefreshing ||
                  !isEditingEnabled
                    ? 0.6
                    : 1,
              }}
            >
              <DottedRefreshIcon
                className={isUploadingLocalExcel ? "loading-spinner" : undefined}
              />
              Refresh Now
            </button>
          </div>
        </div>
      </Modal>
      <UserMemoryDrawer
        open={showMemoryModal}
        setOpen={setShowMemoryModal}
        portalDocument={targetDocument}
      />
    </>
  );
};
